import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Trash, type Icon } from '@phosphor-icons/react';
import { ImmoButton } from './Button';
import { useDialogFokus } from './fokus';

/**
 * Rückfrage vor folgenreichen Aktionen (z. B. Objekt löschen).
 * Fokus startet auf „Abbrechen“; Escape und Klick auf den Hintergrund schließen.
 */
export interface BestaetigenDialogProps {
  titel: string;
  text: string;
  /** Zusatzzeile, z. B. Name und Adresse des Objekts */
  detail?: React.ReactNode;
  bestaetigenLabel: string;
  icon?: Icon;
  gefahr?: boolean;
  laeuft?: boolean;
  onBestaetigen: () => void;
  onAbbrechen: () => void;
  /** Beschriftung des Abbrechen-Knopfs (Standard „Abbrechen“) */
  abbrechenLabel?: string;
}

export function BestaetigenDialog({ titel, text, detail, bestaetigenLabel, icon: IconKomp = Trash, gefahr = true, laeuft = false, onBestaetigen, onAbbrechen, abbrechenLabel = 'Abbrechen' }: BestaetigenDialogProps) {
  // vor dem Fokus auf „Abbrechen“: merkt sich den Auslöser und gibt ihm beim Schließen den Fokus zurück
  useDialogFokus<HTMLDivElement>();
  const abbrechen = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    abbrechen.current?.focus();
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !laeuft) onAbbrechen(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onAbbrechen, laeuft]);

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 450, background: 'rgba(0,0,0,0.35)', fontFamily: "'Manrope', -apple-system, sans-serif" }}
      onMouseDown={e => { if (e.target === e.currentTarget && !laeuft) onAbbrechen(); }}
    >
      <div role="alertdialog" aria-modal="true" aria-labelledby="immo-dialog-titel" aria-describedby="immo-dialog-text"
        className="immo-glas-stark immo-side-in flex flex-col w-full" style={{ maxWidth: 400, borderRadius: 26, padding: 22, gap: 14, color: 'var(--ink)' }}>
        <div className="flex items-center" style={{ gap: 12 }}>
          <span className="flex items-center justify-center shrink-0" style={{ width: 42, height: 42, borderRadius: 14, background: gefahr ? 'var(--bad-soft)' : 'var(--acc-soft)' }}>
            <IconKomp weight="fill" size={20} color={gefahr ? 'var(--bad)' : 'var(--acc)'} />
          </span>
          <div className="flex flex-col" style={{ gap: 2 }}>
            <span id="immo-dialog-titel" style={{ fontSize: 16, fontWeight: 800 }}>{titel}</span>
            <span id="immo-dialog-text" style={{ fontSize: 12, color: 'var(--ink3)' }}>{text}</span>
          </div>
        </div>
        {detail && <div className="immo-innen" style={{ borderRadius: 16, padding: '12px 14px', fontSize: 13 }}>{detail}</div>}
        <div className="flex" style={{ gap: 10 }}>
          <ImmoButton ref={abbrechen} art="fuellung" groesse={44} pille={false} onClick={onAbbrechen} disabled={laeuft} style={{ flex: 1 }}>{abbrechenLabel}</ImmoButton>
          <ImmoButton art={gefahr ? 'bad' : 'akzent'} groesse={44} pille={false} icon={IconKomp} onClick={onBestaetigen} disabled={laeuft} style={{ flex: 1 }}>
            {laeuft ? 'Bitte warten …' : bestaetigenLabel}
          </ImmoButton>
        </div>
      </div>
    </div>,
    document.body,
  );
}
